import React, { useState, useEffect } from "react";
import { Button, Form, FormGroup, Input, Label, Alert, Spinner } from "reactstrap";
import axios from "axios";
import Select, { createFilter } from "react-select";
import { getAuthHeaders, getCurrentUser } from "../../../utils/auth";
import {
  API_URL_comision_multi,
  API_URL_proyecto_con_causa,
} from "../../../constants";

const ProyectoConCausaForm = ({
  proyectoCode,
  proyectoName,
  proyectoMoneda,
  estudiantesIngreso,
  admin = null,
  resetState,
  toggle
}) => {
  const [formData, setFormData] = useState({
    pk: 0,
    pago_por: 'Proyecto con Causa',
    iva: 0,
    retencion: 0,
    isr: 0,
    subcantidad: 0,
    cantidad: 0,
    instructor: '',
    comision_multi_code: '',
    adminCostPercentage: '',
    moneda: '',
    tipo_cambio: null,
    proyecto_code: '',
    proyecto: '',
    mod_user: ''
  });

  const [pccs, setPccs] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const username = getCurrentUser();
    const headers = getAuthHeaders();

    // Fetch proyecto con causa list
    axios.get(API_URL_proyecto_con_causa, { headers })
      .then(response => {
        setPccs(response.data);
      })
      .catch(error => {
        console.error('Error fetching proyecto con causa:', error);
      });

    // Set project data including inherited currency
    setFormData(prev => ({
      ...prev,
      proyecto_code: proyectoCode,
      proyecto: proyectoName,
      moneda: proyectoMoneda || 'USD', // Inherit from project
      mod_user: username
    }));

    // If editing, load admin data
    if (admin) {
      const {
        pk,
        instructor,
        proyecto,
        proyecto_code,
        comision_multi_code,
        cantidad,
        iva,
        isr,
        retencion,
        subcantidad,
        tipo_cambio
      } = admin;

      setFormData({
        pk: pk || 0,
        instructor: instructor || '',
        moneda: proyectoMoneda || 'USD', // Use project currency
        tipo_cambio: tipo_cambio ?? null,
        proyecto: proyecto || proyectoName,
        proyecto_code: proyecto_code || proyectoCode,
        comision_multi_code: comision_multi_code || '',
        cantidad: cantidad || 0,
        iva: iva || 0,
        isr: isr || 0,
        retencion: retencion || 0,
        subcantidad: subcantidad || 0,
        adminCostPercentage: estudiantesIngreso ? (subcantidad / estudiantesIngreso * 100) : 0,
        pago_por: 'Proyecto con Causa',
        mod_user: username
      });
    }
  }, [proyectoCode, proyectoName, proyectoMoneda, admin, estudiantesIngreso]);

  const generateRandomString = (length) => {
    const characters = 'ABCDEFGHJKLMNOPQRSTUVWXYZ23456789';
    let random_string = '';
    for (let i = 0; i < length; i++) {
      random_string += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return random_string;
  };

  const validateForm = () => {
    const errors = {};
    const { instructor, adminCostPercentage } = formData;

    if (!instructor || instructor.trim() === '') {
      errors.instructor = 'Por favor selecciona un Proyecto con Causa';
    }

    if (!adminCostPercentage || adminCostPercentage === '' || parseFloat(adminCostPercentage) === 0) {
      errors.adminCostPercentage = 'Por favor ingresa el porcentaje';
    }

    // moneda validation removed - inherited from project

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setErrorMessage('Por favor completa todos los campos obligatorios marcados con *');
      setTimeout(() => {
        const firstError = document.querySelector('.is-invalid-custom');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = firstError.querySelector('input, select');
          if (input) input.focus();
        }
      }, 100);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const headers = getAuthHeaders();

    try {
      // Calculate subcantidad and cantidad
      const subTotal = estudiantesIngreso * parseFloat(formData.adminCostPercentage) / 100;

      // Prepare data
      const submitData = {
        instructor: formData.instructor,
        moneda: formData.moneda,
        tipo_cambio: formData.tipo_cambio,
        proyecto: formData.proyecto,
        proyecto_code: formData.proyecto_code,
        comision_multi_code: admin ? formData.comision_multi_code : generateRandomString(5),
        cantidad: subTotal,
        iva: parseFloat(formData.iva),
        isr: parseFloat(formData.isr),
        retencion: parseFloat(formData.retencion),
        subcantidad: subTotal,
        pago_por: formData.pago_por,
        mod_user: formData.mod_user
      };

      // Create or update
      if (admin) {
        // ✅ FIXED: Added trailing slash
        const url = `${API_URL_comision_multi}${formData.pk}/`;
        await axios.put(url, submitData, { headers });
      } else {
        await axios.post(API_URL_comision_multi, submitData, { headers });
      }

      resetState();
      toggle();
    } catch (error) {
      console.error("Error saving Proyecto con Causa:", error);

      // Handle server-side validation errors
      if (error.response?.data) {
        const serverErrors = error.response.data;
        if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
          setValidationErrors(serverErrors);
          setErrorMessage('Error de validación. Por favor revisa los campos.');
        } else {
          setErrorMessage('Error al guardar. Por favor verifica los datos.');
        }
      } else {
        setErrorMessage('Error de conexión. Por favor intenta nuevamente.');
      }
      setIsSubmitting(false);
    }
  };

  const h3Style = { color: 'blue' };
  const calculatedAmount = estudiantesIngreso * parseFloat(formData.adminCostPercentage || 0) / 100;

  // Filter option for react-select (search without accents)
  const filterOption = createFilter({
    ignoreAccents: true,
    ignoreCase: true,
    trim: true,
    matchFrom: "any",
  });

  // Options for Select components
  const pccOptions = Array.isArray(pccs)
    ? pccs.map(pcc => ({ value: pcc.nombre, label: pcc.nombre }))
    : [];

  const fxRowCurrency = formData.moneda || 'USD';
  const fxProjectCurrency = proyectoMoneda || 'USD';
  const fxCurrency = fxRowCurrency !== 'USD' ? fxRowCurrency : fxProjectCurrency;
  const showTipoCambio = fxRowCurrency !== 'USD' || fxProjectCurrency !== 'USD';

  return (
    <div>
      <h3>{proyectoName}</h3>
      {proyectoCode && <h3 style={h3Style}>{proyectoCode.toUpperCase()}</h3>}
      <h5>
        Ingreso del Proyecto: {estudiantesIngreso?.toLocaleString(undefined, { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        })}
      </h5>
      <div className="form-requirements-note">
        <strong>Nota:</strong> Los campos marcados con <span className="required-asterisk">*</span> son obligatorios.
      </div>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="instructor">
            Proyecto con Causa<span className="required-asterisk">*</span>
          </Label>
          <Select
            options={pccOptions}
            value={pccOptions.find((o) => o.value === formData.instructor) || null}
            onChange={(opt) => {
              setFormData(prev => ({ ...prev, instructor: opt ? opt.value : "" }));
              setValidationErrors(prev => ({ ...prev, instructor: '' }));
            }}
            placeholder="Selecciona donador"
            isClearable
            filterOption={filterOption}
            className={validationErrors.instructor ? 'is-invalid' : ''}
          />
          {validationErrors.instructor && (
            <div className="invalid-feedback-custom">{validationErrors.instructor}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="adminCostPercentage">
            Porcentaje<span className="required-asterisk">*</span>
          </Label>
          <Input
            type="number"
            name="adminCostPercentage"
            onChange={onChange}
            value={formData.adminCostPercentage}
            className={validationErrors.adminCostPercentage ? 'is-invalid-custom' : ''}
            step="0.01"
            placeholder="0.00"
          />
          {validationErrors.adminCostPercentage && (
            <div className="invalid-feedback-custom">{validationErrors.adminCostPercentage}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="moneda">Moneda (desde proyecto):</Label>
          <Input
            type="text"
            name="moneda"
            value={formData.moneda || 'USD'}
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small className="form-text text-muted">
            La moneda se hereda del proyecto y no se puede cambiar aquí
          </small>
        </FormGroup>

        {showTipoCambio && (
          <FormGroup>
            <Label for="tipo_cambio">
              Tipo de Cambio ({fxCurrency} por 1 USD):
            </Label>
            <Input
              type="number"
              step="0.0001"
              min="0"
              name="tipo_cambio"
              placeholder="ej. 17 (vacío para usar la tasa mensual)"
              onChange={(e) => setFormData(prev => ({
                ...prev,
                tipo_cambio: e.target.value === "" ? null : Number(e.target.value),
              }))}
              value={formData.tipo_cambio ?? ""}
            />
            <small className="form-text text-muted">
              {fxRowCurrency !== 'USD'
                ? 'Opcional. Si lo capturas, esta partida se convierte a USD dividiendo entre este número. Vacío = tasa del mes de inicio del proyecto.'
                : `Opcional. Si lo capturas, esta partida en USD se convierte a ${fxProjectCurrency} multiplicando por este número. Vacío = tasa del mes de inicio del proyecto.`}
            </small>
          </FormGroup>
        )}

        <FormGroup>
          <Label for="costo">Aportacion Proyecto con Causa:</Label>
          <h5>
            {calculatedAmount.toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </h5>
        </FormGroup>

        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
        
        <Button color="primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" /> Guardando...
            </>
          ) : (
            'Guardar'
          )}
        </Button>
      </Form>
    </div>
  );
};

export default ProyectoConCausaForm;